/**
 * ProtectYourAccess — bespoke page for the "Protect Your Access" category (s111 Build 6
 * UX polish). Mounted category-level in GenericCategoryPage (like WhereIsEverything),
 * replacing the flat generic card renderer for this one category.
 *
 * Two sections with explanatory copy (placeholder — Reade polishes later):
 *   A. "Set up your Secure Information File" — the .oesif trust moment + secure_file_location.
 *   B. "Your phone and email" — phone_unlock, phone_location, primary_email, primary_email_password.
 *
 * Completion is UNAFFECTED by the bespoke render: the 3 non-Cat-A fields save to
 * `generic_entries` via the same upsert the generic renderer uses (feeding
 * computeSectionProgress / the section pill AND the server ERS essential #1), and the 2
 * Cat A fields route through <SensitiveFieldInput> exactly as elsewhere. After each save
 * we invalidate the section-overview + unified/progress caches so the pill + essential
 * refresh.
 *
 * Issue 5 — inline .oesif flow (this page suppresses the per-field popup):
 *   - No file yet (secure_file_setup_at null + session not set up): Section A shows a
 *     "Create Your Secure File" CTA (opens the shared SecureFileOnboardingStep inline);
 *     Section B's 2 Cat A fields are locked (disabled + guidance) until the file exists.
 *   - File exists: Section A shows the location field + "Open File"; Cat A fields active.
 *   The popup still fires on Cat A fields ELSEWHERE (Gov IDs, etc.) — only this page opts
 *   out, via SensitiveFieldInput's suppressOnboardingPrompt prop.
 */

import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ShieldCheck, Lock, FileKey } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FieldDeclineControl } from "@/components/FieldDeclineControl";
import { type DeclineReason } from "@shared/constants/decline-reason";
import { useToast } from "@/hooks/use-toast";
import { useSensitiveDataSession } from "@/lib/sensitive-data-session-provider";
import { useAuth } from "@/contexts/AuthContext";
import { secureFileName } from "@/lib/secure-file-name";
import { useSecureFileOpen } from "@/components/SecureFileOpenProvider";
import { SensitiveFieldInput } from "@/components/SensitiveFieldInput";
import { SecureFileOnboardingStep } from "@/components/SecureFileOnboardingStep";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import type { GenericCategory, GenericSection, GenericField, GenericEntry } from "@shared/schema";
import { FONT, WARM } from "@shared/brand";

interface ProtectYourAccessProps {
  clientId: string;
  readOnly?: boolean;
  secureFileSetupAt: Date | null;
}

// ── Plain (non-Cat-A) single-record card field: local state + blur save ─────────────
function PlainField({
  field,
  value,
  readOnly,
  onSave,
  declined,
  onToggleDecline,
  declineReason,
  declineNote,
  declinePending,
}: {
  field: GenericField;
  value: string;
  readOnly?: boolean;
  onSave: (fieldId: string, value: string) => Promise<void>;
  /** s155 (D-155.2): "I don't have this" state for this field's entry (value-wins). */
  declined: boolean;
  declineReason?: DeclineReason | null;
  declineNote?: string | null;
  onToggleDecline: (field: GenericField, declined: boolean, reason?: DeclineReason, note?: string | null) => void;
  declinePending?: boolean;
}) {
  const [local, setLocal] = useState(value);
  // Re-sync if the server value changes underneath (e.g. after a refetch).
  const [lastServer, setLastServer] = useState(value);
  if (value !== lastServer) {
    setLastServer(value);
    setLocal(value);
  }
  return (
    <div className="flex flex-col gap-1">
      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-[#2B2A26]">
          {field.name}
          {field.isRequired && <span className="ml-0.5 text-[#B4553F]" aria-hidden="true">*</span>}
        </span>
        {/* Input hidden when declined (s98) — the planner sees "Not available" via the control below. */}
        {!declined && (
          <Input
            type={field.fieldType === "email" ? "email" : "text"}
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={() => { if (!readOnly && local !== value) onSave(field.id, local); }}
            readOnly={readOnly}
            placeholder={field.placeholder ?? undefined}
            className="text-sm"
            data-testid={`pya-field-${field.fieldKey}`}
          />
        )}
      </label>
      {/* s155 (phone_make_model gap fix, D-155.2 ruling a): the SAME shared FieldDeclineControl +
          declineEligible gate the generic renderer uses, wired into PYA's bespoke path — no forked
          logic. Renders nothing unless the field is declineEligible + empty (or already declined). */}
      <FieldDeclineControl
        declined={declined}
        declineReason={declineReason}
        declineNote={declineNote}
        eligible={!!field.declineEligible}
        hasValue={!!value}
        readOnly={!!readOnly}
        pending={declinePending}
        onDecline={(reason, note) => onToggleDecline(field, true, reason, note)}
        onUndo={() => onToggleDecline(field, false)}
        testId={`decline-${field.id}`}
      />
      {field.instructions && (
        <span className="text-xs text-muted-foreground">{field.instructions}</span>
      )}
    </div>
  );
}

export function ProtectYourAccess({ clientId, readOnly, secureFileSetupAt }: ProtectYourAccessProps) {
  const { toast } = useToast();
  const session = useSensitiveDataSession();
  const { user } = useAuth();
  const { openFile } = useSecureFileOpen();
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  // Setup complete when the session was initialised this device OR the server recorded it.
  const setupComplete = session.isSetupComplete || !!secureFileSetupAt;

  // s111: descriptive, stable filename — planner name + creation date (secure_file_setup_at
  // if the file already exists, else today at first creation). See secure-file-name.ts.
  const fileName = secureFileName(user?.fullName, secureFileSetupAt ?? new Date());

  // ── Resolve category → section → fields → entries (self-fetch, like WhereIsEverything) ──
  const { data: category } = useQuery<GenericCategory>({
    queryKey: ["/api/generic-categories/by-slug", CATEGORY_SLUGS.PROTECT_YOUR_ACCESS, clientId],
    queryFn: async () =>
      (await apiRequest("GET", `/api/generic-categories/by-slug/${CATEGORY_SLUGS.PROTECT_YOUR_ACCESS}?clientId=${clientId}`)).json(),
    enabled: !!clientId,
  });
  const categoryType = category?.categoryType;

  const { data: sections = [] } = useQuery<GenericSection[]>({
    queryKey: ["/api/generic-categories", category?.id, "sections"],
    enabled: !!category?.id,
  });
  const sectionId = useMemo(
    () => sections.find((s) => s.slug === SECTION_SLUGS.PROTECT_YOUR_ACCESS)?.id,
    [sections],
  );

  const { data: fields = [] } = useQuery<GenericField[]>({
    queryKey: ["/api/generic-sections", sectionId, "fields"],
    enabled: !!sectionId,
  });

  const { data: entries = [] } = useQuery<GenericEntry[]>({
    queryKey: ["/api/clients", clientId, "generic-entries", categoryType],
    queryFn: async () => {
      const r = await fetch(`/api/clients/${clientId}/generic-entries?categoryType=${categoryType}`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load entries");
      return r.json();
    },
    enabled: !!clientId && !!categoryType,
  });

  const fieldByKey = useMemo(() => {
    const m = new Map<string, GenericField>();
    fields.filter((f) => f.isActive).forEach((f) => m.set(f.fieldKey, f));
    return m;
  }, [fields]);

  const valueOf = (fieldKey: string): string => {
    const f = fieldByKey.get(fieldKey);
    if (!f) return "";
    return entries.find((e) => e.fieldId === f.id && (e.rowId ?? null) === null)?.value ?? "";
  };

  // D-170.6: the two raw apiRequests in this file converted. Both class-safe — the entry write
  // is the (client, field, row) upsert, the setup flag is last-wins — so neither takes a lock.
  const saveFieldMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      await apiRequest("POST", `/api/clients/${clientId}/generic-entries`, body),
  });
  const setupCompleteMutation = useMutation({
    mutationFn: async () =>
      await apiRequest("POST", `/api/clients/${clientId}/secure-file-setup-complete`, {}),
  });

  const invalidateCompletion = () => {
    // The pill + essential read from these — invalidate so they refresh after a save.
    queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "generic-entries", categoryType] });
    queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "section-overview"] });
    queryClient.invalidateQueries({ queryKey: ["/api/unified/progress", clientId] });
  };

  const saveField = async (fieldId: string, value: string) => {
    if (!category?.id || !sectionId) return;
    try {
      // D-170.6: class C — generic_entries upsert on (client, field, row).
      await saveFieldMutation.mutateAsync({
        categoryId: category.id,
        sectionId,
        fieldId,
        rowId: null,
        value,
      });
      invalidateCompletion();
    } catch (err) {
      toast({ title: "Couldn't save that", description: err instanceof Error ? err.message : "Please try again.", variant: "destructive" });
    }
  };

  // ── Per-field "I don't have this" (s98 / D-155.2) — SAME contract as the generic renderer,
  //    scoped to this single-record card (rowId null). computeSectionProgress already treats a
  //    declined entry as resolved, so a decline drops the field from the PYA completion denominator
  //    with no completion-logic change here.
  const [optimisticDeclined, setOptimisticDeclined] = useState<Record<string, boolean>>({});

  const isFieldDeclined = (fieldId: string): boolean => {
    const entry = entries.find((e) => e.fieldId === fieldId && (e.rowId ?? null) === null);
    // Value wins: a field that actually holds a value is never shown as declined.
    if (entry?.value && entry.value.trim() !== "") return false;
    if (fieldId in optimisticDeclined) return optimisticDeclined[fieldId];
    return entry?.declined === true;
  };

  // s179: the reason on a declined PYA field, for the per-reason status line.
  const fieldDeclineReason = (fieldId: string): DeclineReason | null => {
    const entry = entries.find((e) => e.fieldId === fieldId && (e.rowId ?? null) === null);
    return (entry?.declineReason as DeclineReason | undefined) ?? null;
  };

  // s179 rebuild: the planner's location note (card section — rowId always null here).
  const fieldDeclineNote = (fieldId: string): string | null => {
    const entry = entries.find((e) => e.fieldId === fieldId && (e.rowId ?? null) === null);
    return (entry?.declineNote as string | undefined) ?? null;
  };

  const setDeclinedMutation = useMutation({
    mutationFn: async ({ fieldId, declined, reason, note }: { fieldId: string; declined: boolean; reason?: DeclineReason; note?: string | null }) => {
      if (!category?.id || !sectionId) throw new Error("Category not loaded — please refresh the page");
      const res = await apiRequest("POST", `/api/clients/${clientId}/generic-entries`, {
        categoryId: category.id,
        sectionId,
        fieldId,
        rowId: null,
        declined,
        // s179: the reason rides the same single-writer generic-entries POST as the flag.
        ...(declined && reason ? { declineReason: reason } : {}),
        ...(declined && reason && note !== undefined ? { declineNote: note } : {}),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Couldn't update" }));
        throw new Error(err.message || "Couldn't update");
      }
      return res.json();
    },
    onError: (error: Error, vars) => {
      setOptimisticDeclined((prev) => { const next = { ...prev }; delete next[vars.fieldId]; return next; });
      toast({ title: "Couldn't update", description: error.message, variant: "destructive" });
    },
    onSettled: async (_d, _e, vars) => {
      // anti-flicker (s154 1a): await the entries refetch before dropping the optimistic override.
      await queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "generic-entries", categoryType] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "section-overview"] });
      queryClient.invalidateQueries({ queryKey: ["/api/unified/progress", clientId] });
      setOptimisticDeclined((prev) => { const next = { ...prev }; delete next[vars.fieldId]; return next; });
    },
  });

  const toggleDecline = useCallback((field: GenericField, declined: boolean, reason?: DeclineReason, note?: string | null) => {
    if (readOnly) return;
    setOptimisticDeclined((prev) => ({ ...prev, [field.id]: declined }));
    setDeclinedMutation.mutate({ fieldId: field.id, declined, reason, note });
  }, [readOnly, setDeclinedMutation]);

  // ── .oesif setup complete (inline, mirrors SensitiveFieldInput.handleSetupComplete) ──
  const handleSetupComplete = async (handle: FileSystemFileHandle | null, passphrase: string, location?: string) => {
    session.completeSetup(handle, passphrase);
    setOnboardingOpen(false);
    // Persist the file-location description to THIS section's field (not via the endpoint's
    // location param, which targets the field by key and could hit the retired About You one).
    const locField = fieldByKey.get("secure_file_location");
    if (location && locField) await saveField(locField.id, location);
    try {
      // D-170.6: class B — completion flag, last-wins.
      await setupCompleteMutation.mutateAsync();
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
    } catch {
      // Non-fatal — session is set up; the server record re-syncs on next open.
    }
  };

  const phoneUnlock = fieldByKey.get("phone_unlock");
  const phoneMakeModel = fieldByKey.get("phone_make_model"); // FIELD-AUDIT-1 (D-154.5): replaced phone_location
  const primaryEmail = fieldByKey.get("primary_email");
  const primaryEmailPassword = fieldByKey.get("primary_email_password");
  const secureFileLocation = fieldByKey.get("secure_file_location");

  if (!sectionId || fields.length === 0) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  const catAProps = (f: GenericField) => ({
    field: f,
    value: "",
    onChange: () => {},
    onBlur: () => {},
    clientId,
    secureFileSetupAt,
    suppressOnboardingPrompt: true,   // this page drives setup inline
    disabled: !setupComplete,          // locked until the file exists
    readOnly,
  });

  return (
    <div className="space-y-4 pb-4">
      {/* ── Section A — Secure Information File ─────────────────────────────────────── */}
      <section className="rounded-lg border border-stone-200/70 bg-[#FCFAF6] p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-[#4F6B51]" />
          <div>
            <h2 style={{ fontFamily: FONT.serif, fontSize: 20, fontWeight: 500, color: WARM.ink }}>
              Set up your Secure Information File
            </h2>
            <p className="mt-1 max-w-none text-sm leading-relaxed text-[#5C5647]">
              {/* PLACEHOLDER COPY — Reade to polish. */}
              Your most sensitive details — passwords, unlock codes — are stored in a single
              file (<span className="font-medium">.oesif</span>) that lives on
              <span className="font-medium"> your own computer</span>, never on our servers.
              You choose where to keep it; add a passphrase and only you can open it. This is the vault everything
              below is saved into.
            </p>
          </div>
        </div>

        <div className="mt-3 pl-9">
          {!setupComplete ? (
            !readOnly && (
              <Button
                onClick={() => setOnboardingOpen(true)}
                className="bg-[#4F6B51] text-white hover:bg-[#425A44]"
                data-testid="pya-create-secure-file"
              >
                <FileKey className="mr-2 h-4 w-4" /> Create Your Secure File
              </Button>
            )
          ) : (
            <div className="space-y-3">
              {secureFileLocation && (
                <PlainField field={secureFileLocation} value={valueOf("secure_file_location")} readOnly={readOnly} onSave={saveField}
                  declined={isFieldDeclined(secureFileLocation.id)} declineReason={fieldDeclineReason(secureFileLocation.id)} declineNote={fieldDeclineNote(secureFileLocation.id)} onToggleDecline={toggleDecline} declinePending={setDeclinedMutation.isPending} />
              )}
              {!readOnly && (
                <button
                  type="button"
                  onClick={openFile}
                  data-testid="pya-open-file"
                  className="text-[13px] font-medium underline underline-offset-2 text-[#4F6B51] hover:text-[#425A44]"
                >
                  Open File
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── Section B — Phone & email ───────────────────────────────────────────────── */}
      <section className="rounded-lg border border-stone-200/70 bg-[#FCFAF6] p-5">
        <div className="flex items-start gap-3">
          <Lock className="mt-0.5 h-6 w-6 shrink-0 text-[#4F6B51]" />
          <div>
            <h2 style={{ fontFamily: FONT.serif, fontSize: 20, fontWeight: 500, color: WARM.ink }}>
              Your phone and email
            </h2>
            <p className="mt-1 max-w-none text-sm leading-relaxed text-[#5C5647]">
              {/* PLACEHOLDER COPY — Reade to polish. */}
              Your phone holds the two-factor codes for your accounts, and your email is the
              recovery key for almost everything online. Without them, your executor is locked
              out. Record how to get into both.
            </p>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 pl-9 md:grid-cols-2">
          {phoneUnlock && (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-[#2B2A26]">
                {phoneUnlock.name}<span className="ml-0.5 text-[#B4553F]" aria-hidden="true">*</span>
              </span>
              <SensitiveFieldInput {...catAProps(phoneUnlock)} />
              <span className="text-xs text-muted-foreground">Stored in your secure file — doesn't affect completion.</span>
            </div>
          )}
          {phoneMakeModel && (
            <PlainField field={phoneMakeModel} value={valueOf("phone_make_model")} readOnly={readOnly} onSave={saveField}
              declined={isFieldDeclined(phoneMakeModel.id)} declineReason={fieldDeclineReason(phoneMakeModel.id)} declineNote={fieldDeclineNote(phoneMakeModel.id)} onToggleDecline={toggleDecline} declinePending={setDeclinedMutation.isPending} />
          )}
          {primaryEmail && (
            <PlainField field={primaryEmail} value={valueOf("primary_email")} readOnly={readOnly} onSave={saveField}
              declined={isFieldDeclined(primaryEmail.id)} declineReason={fieldDeclineReason(primaryEmail.id)} declineNote={fieldDeclineNote(primaryEmail.id)} onToggleDecline={toggleDecline} declinePending={setDeclinedMutation.isPending} />
          )}
          {primaryEmailPassword && (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-[#2B2A26]">
                {primaryEmailPassword.name}<span className="ml-0.5 text-[#B4553F]" aria-hidden="true">*</span>
              </span>
              <SensitiveFieldInput {...catAProps(primaryEmailPassword)} />
              <span className="text-xs text-muted-foreground">Stored in your secure file — doesn't affect completion.</span>
            </div>
          )}
        </div>
      </section>

      {/* Inline onboarding dialog (this page's Create CTA, not a per-field focus popup). */}
      {onboardingOpen && (
        <SecureFileOnboardingStep
          open={true}
          onOpenChange={setOnboardingOpen}
          fieldName="your secure information"
          fileName={fileName}
          onComplete={handleSetupComplete}
          onSkip={() => setOnboardingOpen(false)}
        />
      )}
    </div>
  );
}
